import { useEffect, useState } from 'react';
import { faStar as faStarRegular } from '@fortawesome/free-regular-svg-icons';
import { faStar as faStarSolid } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as styles from './StarRatingButton.module.scss';

type StarProps = {
  id: number;
  checked: boolean;
  onClick: (id: number, value: number) => void;
  value: number;
  size: number;
  name: string | number;
};

type StarRatingButtonProps = {
  name?: string | number;
  length?: number;
  defaultValue?: number;
  size?: number;
  containerWidth?: number;
  onClick?: (value: number) => void;
};

const Star = ({ id, checked, onClick, value, size, name }: StarProps) => {
  const stringId = `${name}-${id}`;
  const starIcon = checked ? faStarSolid : faStarRegular;
  const iconStyle = { fontSize: size, width: size };
  const labelStyle = { width: size };

  return (
    <>
      <input
        id={stringId}
        type="checkbox"
        aria-label={`star ${stringId}`}
        className={styles.starInput}
        value={id}
        checked={checked}
        onChange={() => onClick(id, value)}
      />
      <label htmlFor={stringId} className={styles.star} style={labelStyle}>
        <FontAwesomeIcon icon={starIcon} style={iconStyle} />
      </label>
    </>
  );
};

const StarRatingButton = ({
  name = 'rating',
  length = 5,
  defaultValue = 0,
  size = 36,
  containerWidth,
  onClick,
}: StarRatingButtonProps) => {
  const starsList = Array.from(Array(length + 1).keys())
    .reverse()
    .slice(0, length);
  const [rating, setRating] = useState<number>(starsList[defaultValue - 1]);
  const containerStyle = { height: size, width: containerWidth || '100%' };
  const handleOnClick = (id: number, value: number) => {
    setRating(id);
    if (!!onClick && typeof onClick === 'function') onClick(value);
  };

  // update rating if defaultValue changes
  useEffect(() => {
    setRating(starsList[defaultValue - 1]);
  }, [defaultValue]);

  return (
    <div className={styles.rating} style={containerStyle} data-testid="rating">
      {starsList.map((star, index) => (
        <Star
          key={index + 1}
          id={index + 1}
          value={star}
          aria-label="star rating button"
          size={size}
          name={name}
          checked={rating !== 0 && rating <= index + 1}
          onClick={handleOnClick}
        />
      ))}
    </div>
  );
};

export default StarRatingButton;
